import { useState, useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { usePermissions, PERMISSIONS } from '@/hooks/usePermissions';
import { useNotificationStore } from './hooks/useNotifications';
import {
  useNotificationsInbox,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
  type InboxNotification,
} from './hooks/useNotificationsInbox';

/**
 * Notifications Inbox page — shows all triggered reminders and system notifications
 * for the current user. Supports search, date range, type, and read/unread filtering.
 */
export function NotificationsInboxPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const [channel, setChannel] = useState('in_app');
  const [selectedNotification, setSelectedNotification] = useState<InboxNotification | null>(null);

  const { data, isLoading } = useNotificationsInbox({
    page,
    search: search || undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    type: type || undefined,
    status: status || undefined,
    channel: channel || undefined,
  });
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();
  const { can } = usePermissions();
  const canUpdate = can(PERMISSIONS.NOTIFICATIONS_UPDATE);

  const notifications = data?.data ?? [];
  const unreadCount = data?.unreadCount ?? 0;
  const total = data?.pagination?.total ?? 0;
  const pageSize = data?.pagination?.pageSize ?? 50;
  const availableTypes = data?.availableTypes ?? [];
  const hasMore = page * pageSize < total;

  // Sync unread count to the notification store AND sidebar badge query
  const queryClient = useQueryClient();
  useEffect(() => {
    if (data?.unreadCount !== undefined) {
      useNotificationStore.getState().setUnreadCount(data.unreadCount);
      // Update the sidebar's unread-count query cache directly
      queryClient.setQueryData(['notifications', 'unread-count'], data.unreadCount);
    }
  }, [data?.unreadCount, queryClient]);

  const handleSearch = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setSearch(searchInput.trim());
      setPage(1);
    },
    [searchInput],
  );

  const handleClearFilters = useCallback(() => {
    setSearch('');
    setSearchInput('');
    setDateFrom('');
    setDateTo('');
    setType('');
    setStatus('');
    setChannel('in_app');
    setPage(1);
  }, []);

  const hasFilters =
    !!search || !!dateFrom || !!dateTo || !!type || !!status || channel !== 'in_app';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Notifications</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Reminders, alerts, and system notifications
            {unreadCount > 0 && (
              <span className="ml-2 inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700 dark:bg-red-900/30 dark:text-red-400">
                {unreadCount} unread
              </span>
            )}
          </p>
        </div>
        {unreadCount > 0 && canUpdate && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => markAllRead.mutate()}
            loading={markAllRead.isPending}
          >
            Mark all as read
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card padding="sm">
        <div className="flex flex-wrap items-end gap-4">
          {/* Search */}
          <form onSubmit={handleSearch} className="flex-1 min-w-[200px]">
            <label className="mb-1.5 block text-xs font-medium text-gray-500 dark:text-gray-400">
              Search
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by title or content..."
                className="flex-1 rounded-lg border border-border bg-white px-3 py-2 text-sm dark:border-border-dark dark:bg-gray-800 dark:text-gray-100 placeholder:text-gray-400"
              />
              <Button type="submit" size="sm" variant="outline">
                Search
              </Button>
            </div>
          </form>

          {/* Type filter */}
          <div className="w-48">
            <label className="mb-1.5 block text-xs font-medium text-gray-500 dark:text-gray-400">
              Type
            </label>
            <select
              value={type}
              onChange={(e) => {
                setType(e.target.value);
                setPage(1);
              }}
              className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm dark:border-border-dark dark:bg-gray-800 dark:text-gray-100"
            >
              <option value="">All types</option>
              {availableTypes.map((t) => (
                <option key={t} value={t}>
                  {formatTypeLabel(t)}
                </option>
              ))}
            </select>
          </div>

          {/* Channel filter */}
          <div className="w-36">
            <label className="mb-1.5 block text-xs font-medium text-gray-500 dark:text-gray-400">
              Channel
            </label>
            <select
              value={channel}
              onChange={(e) => {
                setChannel(e.target.value);
                setPage(1);
              }}
              className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm dark:border-border-dark dark:bg-gray-800 dark:text-gray-100"
            >
              <option value="in_app">In-App</option>
              <option value="email">Email</option>
              <option value="all">All</option>
            </select>
          </div>

          {/* Read/Unread filter */}
          <div className="w-36">
            <label className="mb-1.5 block text-xs font-medium text-gray-500 dark:text-gray-400">
              Status
            </label>
            <select
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(1);
              }}
              className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm dark:border-border-dark dark:bg-gray-800 dark:text-gray-100"
            >
              <option value="">All</option>
              <option value="unread">Unread</option>
              <option value="read">Read</option>
            </select>
          </div>

          {/* Date From */}
          <div className="w-40">
            <label className="mb-1.5 block text-xs font-medium text-gray-500 dark:text-gray-400">
              From
            </label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setPage(1);
              }}
              className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm dark:border-border-dark dark:bg-gray-800 dark:text-gray-100"
            />
          </div>

          {/* Date To */}
          <div className="w-40">
            <label className="mb-1.5 block text-xs font-medium text-gray-500 dark:text-gray-400">
              To
            </label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setPage(1);
              }}
              className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm dark:border-border-dark dark:bg-gray-800 dark:text-gray-100"
            />
          </div>

          {/* Clear */}
          {hasFilters && (
            <Button variant="ghost" size="sm" onClick={handleClearFilters}>
              Clear filters
            </Button>
          )}
        </div>
      </Card>

      {/* Notifications list */}
      <Card>
        <CardHeader title={`Results (${total})`} />
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Loading notifications...</p>
          ) : notifications.length > 0 ? (
            <ul role="list" className="divide-y divide-border dark:divide-border-dark">
              {notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onSelect={() => {
                    setSelectedNotification(notification);
                    // Opening an unread notification marks it read.
                    if (!notification.readAt && canUpdate) {
                      markRead.mutate(notification.id);
                    }
                  }}
                  onMarkRead={() => markRead.mutate(notification.id)}
                  canUpdate={canUpdate}
                />
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {hasFilters
                ? 'No notifications match your filters.'
                : 'No notifications yet. Triggered reminders and system alerts will appear here.'}
            </p>
          )}

          {/* Pagination */}
          {total > pageSize && (
            <div className="mt-4 flex items-center justify-between border-t border-border pt-4 dark:border-border-dark">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              <span className="text-sm text-gray-500">
                Page {page} of {Math.ceil(total / pageSize)}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={!hasMore}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Notification detail popup — shows title, body, and attached metadata */}
      <NotificationDetailModal
        notification={selectedNotification}
        onClose={() => setSelectedNotification(null)}
      />
    </div>
  );
}

/**
 * Modal showing a single notification's full details plus any metadata (`data`)
 * attached to it, rendered as readable key/value pairs.
 */
function NotificationDetailModal({
  notification,
  onClose,
}: {
  notification: InboxNotification | null;
  onClose: () => void;
}) {
  if (!notification) return null;

  const formattedDate = new Date(notification.createdAt).toLocaleString('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const metadataEntries = Object.entries(notification.data ?? {});

  const formatKey = (key: string): string =>
    key.replace(/[._]/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

  const formatValue = (value: unknown): string => {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  };

  return (
    <Modal open onClose={onClose} title={notification.title} size="lg">
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={getTypeVariant(notification.type)} size="sm">
            {formatTypeLabel(notification.type)}
          </Badge>
          <span className="text-xs text-gray-400 dark:text-gray-500">{formattedDate}</span>
        </div>

        {notification.body && (
          <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
            {notification.body}
          </p>
        )}

        <div>
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            Details
          </h4>
          {metadataEntries.length > 0 ? (
            <dl className="divide-y divide-border rounded-lg border border-border dark:divide-border-dark dark:border-border-dark">
              {metadataEntries.map(([key, value]) => (
                <div key={key} className="grid grid-cols-3 gap-3 px-3 py-2">
                  <dt className="text-xs font-medium text-gray-500 dark:text-gray-400">
                    {formatKey(key)}
                  </dt>
                  <dd className="col-span-2 break-words text-sm text-gray-900 dark:text-gray-100">
                    {typeof value === 'object' && value !== null ? (
                      <pre className="whitespace-pre-wrap text-xs">{formatValue(value)}</pre>
                    ) : (
                      formatValue(value)
                    )}
                  </dd>
                </div>
              ))}
            </dl>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No additional details attached to this notification.
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
}

function NotificationItem({
  notification,
  onSelect,
  onMarkRead,
  canUpdate,
}: {
  notification: InboxNotification;
  onSelect: () => void;
  onMarkRead: () => void;
  canUpdate: boolean;
}) {
  const isUnread = !notification.readAt;
  const date = new Date(notification.createdAt);
  const formattedDate = date.toLocaleDateString('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const typeLabel = formatTypeLabel(notification.type);
  const typeVariant = getTypeVariant(notification.type);

  return (
    <li
      className={`flex items-start gap-3 py-3 ${isUnread ? 'bg-brand-50/50 dark:bg-brand-900/10 -mx-4 px-4 rounded' : ''}`}
    >
      {/* Unread indicator */}
      <div className="mt-1.5 shrink-0">
        {isUnread ? (
          <span className="block h-2.5 w-2.5 rounded-full bg-brand-500" aria-label="Unread" />
        ) : (
          <span className="block h-2.5 w-2.5 rounded-full bg-transparent" />
        )}
      </div>

      {/* Content — click to open the detail popup with attached metadata */}
      <button
        type="button"
        onClick={onSelect}
        className="flex-1 min-w-0 text-left rounded focus:outline-none focus:ring-2 focus:ring-brand-500"
        aria-label={`View details for ${notification.title}`}
      >
        <div className="flex items-center gap-2 mb-0.5">
          <Badge variant={typeVariant} size="sm">
            {typeLabel}
          </Badge>
          <time
            dateTime={notification.createdAt}
            className="text-xs text-gray-400 dark:text-gray-500"
          >
            {formattedDate}
          </time>
        </div>
        <p
          className={`text-sm hover:underline ${isUnread ? 'font-medium text-gray-900 dark:text-gray-100' : 'text-gray-700 dark:text-gray-300'}`}
        >
          {notification.title}
        </p>
        {notification.body && (
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
            {notification.body}
          </p>
        )}
      </button>

      {/* Mark as read */}
      {isUnread && canUpdate && (
        <button
          type="button"
          onClick={onMarkRead}
          className="shrink-0 rounded p-1 text-gray-400 hover:text-brand-600 dark:hover:text-brand-400"
          aria-label="Mark as read"
          title="Mark as read"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </button>
      )}
    </li>
  );
}

function formatTypeLabel(type: string): string {
  switch (type) {
    case 'reminder_due':
      return 'Reminder';
    case 'job_overdue':
      return 'Job Overdue';
    case 'job_due_date_reminder':
      return 'Due Date';
    case 'sla_escalation':
      return 'SLA Alert';
    case 'auth.password_reset_requested':
      return 'Password Reset';
    default:
      return type.replace(/[._]/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  }
}

function getTypeVariant(type: string): 'default' | 'warning' | 'danger' | 'info' | 'success' {
  switch (type) {
    case 'reminder_due':
      return 'info';
    case 'job_overdue':
      return 'danger';
    case 'job_due_date_reminder':
      return 'warning';
    case 'sla_escalation':
      return 'danger';
    default:
      return 'default';
  }
}
